'use client';

import { useCallback, useEffect, useState } from 'react';

export function useFaiProductSelection<T extends { gencode: string }>(
    products: T[],
    maxProducts: number,
    initialSelection: string[] = [],
) {
    const [selectedGencode, setSelectedGencode] = useState<string[]>(() => {
        if (initialSelection.length) {
            return initialSelection.slice(0, maxProducts);
        }

        return products.slice(0, maxProducts).map((p) => p.gencode);
    });

    const selectedProducts = selectedGencode
        .map((gencode) => products.find((product) => product.gencode === gencode))
        .filter(Boolean) as T[];

    const remainingSlots = Math.max(0, maxProducts - selectedProducts.length);

    const handleChangeSelectedGencode = useCallback((index: number, gencode?: string) => {
        if (!gencode) return;

        setSelectedGencode((prev) => {
            const newSelection = [...prev];
            newSelection[index] = gencode;
            return newSelection;
        });
    }, []);

    const isOptionDisabled = useCallback(
        (gencode: string) => selectedProducts.some((p) => p.gencode === gencode),
        [selectedProducts],
    );

    // Auto-adjust selection when maxProducts, products or initial selection change
    useEffect(() => {
        setSelectedGencode((prev) => {
            const availableGencodes = products.map((p) => p.gencode);
            let newSelection = (prev.length ? prev : initialSelection)
                .filter((gencode) => availableGencodes.includes(gencode))
                .slice(0, maxProducts);

            if (newSelection.length > maxProducts) {
                newSelection = newSelection.slice(0, maxProducts);
            } else if (newSelection.length < maxProducts && initialSelection.length === 0) {
                const toAdd = availableGencodes
                    .filter((gencode) => !newSelection.includes(gencode))
                    .slice(0, maxProducts - newSelection.length);
                newSelection = [...newSelection, ...toAdd];
            }
            return newSelection;
        });
    }, [initialSelection, maxProducts, products]);

    return {
        selectedGencode,
        selectedProducts,
        remainingSlots,
        handleChangeSelectedGencode,
        isOptionDisabled,
        setSelectedGencode,
    };
}
